import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { toast } from 'sonner';
import { Loader2, Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const MarketBriefsManager = () => {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrief, setEditingBrief] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    category: 'market_analysis',
    slug: '',
    published: false
  });

  useEffect(() => {
    fetchBriefs();
  }, []);

  const fetchBriefs = async () => {
    try {
      // Fetch all briefs (both draft and published) for admin
      const data = await apiClient.get('/api/market-briefs/?limit=100');
      setBriefs(data);
    } catch (error) {
      toast.error('Грешка при зареждане');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (briefId) => {
    try {
      await apiClient.post(`/api/market-briefs/${briefId}/publish`, {});
      toast.success('Анализът е публикуван');
      fetchBriefs();
    } catch (error) {
      toast.error('Грешка при публикуване');
    }
  };

  const handleUnpublish = async (briefId) => {
    try {
      await apiClient.post(`/api/market-briefs/${briefId}/unpublish`, {});
      toast.success('Анализът е върнат в чернова');
      fetchBriefs();
    } catch (error) {
      toast.error('Грешка при промяна на статуса');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const briefData = {
        ...formData,
        slug: formData.slug || formData.title.toLowerCase().replace(/\s+/g, '-')
      };

      if (editingBrief) {
        await apiClient.put(`/api/market-briefs/${editingBrief._id}`, briefData);
        toast.success('Анализ обновен');
      } else {
        await apiClient.post('/api/market-briefs/', briefData);
        toast.success('Анализ създаден');
      }

      setIsModalOpen(false);
      resetForm();
      fetchBriefs();
    } catch (error) {
      toast.error(error.message || 'Грешка при запазване');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      summary: '',
      content: '',
      category: 'market_analysis',
      slug: '',
      published: false
    });
    setEditingBrief(null);
  };

  const handleEdit = (brief) => {
    setEditingBrief(brief);
    setFormData({
      title: brief.title,
      summary: brief.summary,
      content: brief.content,
      category: brief.category,
      slug: brief.slug,
      published: brief.published
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Сигурни ли сте?')) return;
    
    try {
      await apiClient.delete(`/api/market-briefs/${id}`);
      toast.success('Анализ изтрит');
      fetchBriefs();
    } catch (error) {
      toast.error('Грешка при изтриване');
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      if (currentStatus === 'published' || currentStatus === true) {
        await apiClient.post(`/api/market-briefs/${id}/unpublish`, {});
        toast.success('Анализ върнат в чернова');
      } else {
        await apiClient.post(`/api/market-briefs/${id}/publish`, {});
        toast.success('Анализ публикуван');
      }
      fetchBriefs();
    } catch (error) {
      toast.error('Грешка при промяна на статуса');
    }
  };

  if (loading && briefs.length === 0) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-semibold text-[#F6F2E8]">Пазарни анализи</h1>
          <p className="text-[#A9A9A9] mt-1">Market Briefs & Insights</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" /> Нов анализ
        </Button>
      </div>

      {/* Briefs List */}
      <div className="space-y-4">
        {briefs.map((brief) => (
          <div key={brief._id} className="bg-[#111111] border border-[#D4AF37]/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-xl font-serif font-semibold text-[#F6F2E8]">{brief.title}</h3>
                  <span className="text-xs px-2 py-1 rounded bg-[#D4AF37]/20 text-[#D4AF37]">
                    {brief.category}
                  </span>
                  {(brief.status === 'published' || brief.published) ? (
                    <span className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      Публикуван
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">
                      <AlertCircle className="w-3 h-3" />
                      Чернова
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#A9A9A9] mb-3">{brief.summary}</p>
                <p className="text-xs text-[#A9A9A9]">
                  {new Date(brief.date).toLocaleDateString('bg-BG', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => togglePublish(brief._id, brief.published)}
                  className="p-2 bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10 transition-colors"
                  title={brief.published ? 'Скрий' : 'Публикувай'}
                >
                  {brief.published ? <EyeOff size={16} className="text-[#A9A9A9]" /> : <Eye size={16} className="text-[#D4AF37]" />}
                </button>
                <button
                  onClick={() => handleEdit(brief)}
                  className="p-2 bg-[#0B0B0B] rounded hover:bg-[#D4AF37]/10 transition-colors"
                  title="Редактирай"
                >
                  <Edit2 size={16} className="text-[#D4AF37]" />
                </button>
                <button
                  onClick={() => handleDelete(brief._id)}
                  className="p-2 bg-[#0B0B0B] rounded hover:bg-red-500/10 transition-colors"
                  title="Изтрий"
                >
                  <Trash2 size={16} className="text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#111111] border-[#D4AF37]/20 text-[#F6F2E8] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">
              {editingBrief ? 'Редактирай анализ' : 'Нов пазарен анализ'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Заглавие *</Label>
              <Input
                required
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20"
                placeholder="Пазарен преглед Q1 2026"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Категория *</Label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full h-10 px-3 rounded-md bg-[#0B0B0B] border border-[#D4AF37]/20 text-[#F6F2E8]"
                >
                  <option value="market_analysis">Пазарен анализ</option>
                  <option value="investment_tips">Инвестиционни съвети</option>
                  <option value="legal_updates">Правни промени</option>
                </select>
              </div>

              <div>
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20"
                  placeholder="pazaren-pregled-q1-2026"
                />
              </div>
            </div>

            <div>
              <Label>Резюме *</Label>
              <Textarea
                required
                rows={3}
                value={formData.summary}
                onChange={(e) => setFormData({...formData, summary: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none"
                placeholder="Кратко резюме на анализа (2-3 изречения)..."
              />
            </div>

            <div>
              <Label>Съдържание *</Label>
              <Textarea
                required
                rows={12}
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                className="bg-[#0B0B0B] border-[#D4AF37]/20 resize-none"
                placeholder="Пълно съдържание на анализа..."
              />
            </div>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm">Публикувай веднага</span>
            </label>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary"
              >
                Отказ
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingBrief ? 'Обнови' : 'Създай')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketBriefsManager;